import { roundToGrid } from "@examspark/common";
import { Stem, Cartesian, StemNumberLinePlot } from "@examspark/types";

import { getNewScore } from "..";

import { scoreShape } from "./common";

export const scoreNumberLine = (
  expected: StemNumberLinePlot,
  actual: Partial<Stem.Response>,
) => {
  const { scoring } = expected;
  const score = getNewScore();

  // uniq shapes before scoring since the editor can be confusing
  // if a student or item author draws the same shape multiple times
  const expectedShapes = uniqueShapes(
    scoring?.shapes?.map((shape) =>
      coerceShape(shape, expected.config.numberLine),
    ) ?? [],
  );
  const actualShapes = uniqueShapes(
    actual?.shapes?.map((shape) =>
      coerceShape(shape, expected.config.numberLine),
    ) ?? [],
  );

  score.max = expectedShapes?.length || 0;

  if (!expectedShapes) return score;
  if (!actualShapes) return score;

  // Note: We are looping over EXPECTED shapes first, and checking if
  // there is a student shape that matches it. This means that if a student
  // responds with the same correct shape multiple times, they will only
  // get credit for it once.
  for (const expectedShape of expectedShapes) {
    const correct = actualShapes.some((actualShape) => {
      return scoreShape(expectedShape, actualShape);
    });

    // 1 point per correct shape
    if (correct) {
      score.actual++;
    }
  }

  // -1 points for each extra shape (> expected count. So a question
  // with 3 shapes, but 4 shapes drawn, will get penalized 1 point)
  if (actualShapes.length > expectedShapes.length) {
    score.actual = score.actual - (actualShapes.length - expectedShapes.length);
  }

  // Don't allow negative scores
  if (score.actual < 0) {
    score.actual = 0;
  }

  return score;
};

// funnily enough, scoreShape is also a great way to deduplicate shapes
const uniqueShapes = (shapes: Cartesian.Shape[]) => {
  return shapes.filter((shape, index, self) => {
    return index === self.findIndex((s) => scoreShape(s, shape));
  });
};

// round coords to the grid to avoid floating point errors
// as well as handle old, forcefully rounded coords from the old graphing tool
const coerceShape = (
  shape: Cartesian.Shape,
  axis: Cartesian.Axis,
): Cartesian.Shape => {
  if ("coords" in shape) {
    return {
      ...shape,
      coords: shape.coords.map((coord) => ({
        ...coord,
        x: roundToGrid(coord.x, axis),
      })),
    };
  } else {
    return {
      ...shape,
      coord: {
        ...shape.coord,
        x: roundToGrid(shape.coord.x, axis),
      },
    };
  }
};
